import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { PushNotifications, PermissionStatus } from '@capacitor/push-notifications';
import { UserService } from './user.service';
import { ENV_CONFIG } from '../../bin/env.config';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { SalonmonsterHttpClient } from './salonmonster-http-client';
import { ErrorHandlerService } from './error-handler.service';
import { Utilities } from '../utilities/utilities';

export interface DebugLog {
  timestamp: string;
  type: 'registration' | 'topic' | 'notification' | 'error' | 'permission' | 'info';
  message: string;
  data?: any;
}

export interface DebugInfo {
  deviceToken?: string;
  deviceTokenMasked?: string;
  stylistID?: number;
  platform: string;
  permissionStatus?: PermissionStatus;
  registered: boolean;
  subscribedTopics: string[];
  recentNotifications: any[];
  logs: DebugLog[];
}

@Injectable({
  providedIn: 'root'
})
export class NotificationDebugService extends SalonmonsterHttpClient {
  private readonly STORAGE_KEY = 'fcm_debug_logs';
  private readonly MAX_LOGS = 1000;
  private readonly MAX_NOTIFICATIONS = 10;
  private deviceToken: string | null = null;
  private isRegisteredWithBackend: boolean = false;
  private subscribedTopics: string[] = [];
  private recentNotifications: any[] = [];

  constructor(
    private platform: Platform,
    protected http: HttpClient,
    protected userService: UserService,
    protected errorHandlerService: ErrorHandlerService
  ) {
    super(http, userService, errorHandlerService);
    this.loadFromStorage();
  }

  /**
   * Check if debug mode is enabled
   */
  private isDebugEnabled(): boolean {
    return (environment as any).enableNotificationDebug === true;
  }

  /**
   * Log registration attempt/result
   */
  logRegistration(deviceToken: string | null, success: boolean, error?: any): void {
    if (!this.isDebugEnabled()) return;

    // Update token and registration status regardless of whether we log
    if (deviceToken) {
      this.deviceToken = deviceToken;
      // Only mark as registered if this was a successful backend registration
      // The 'success' parameter indicates backend registration success
      if (success) {
        // Explicitly set to true for successful backend registration
        this.isRegisteredWithBackend = true;
      }
      // Save immediately after setting the flag
      this.saveToStorage();
    } else if (!success) {
      // If no token and registration failed, mark as not registered
      this.isRegisteredWithBackend = false;
      this.saveToStorage();
    }

    // Only log if there's an actual error, or if it's a successful registration
    // Don't log "failed" when we're just retrieving a token (success: false, no error)
    if (!success && !error) {
      // This is just token retrieval, not a registration attempt - don't log it
      return;
    }

    const message = success
      ? `Device token registered successfully`
      : `Device token registration failed: ${error?.message || 'Unknown error'}`;

    this.addLog('registration', message, {
      deviceToken: deviceToken ? this.maskToken(deviceToken) : null,
      success,
      error: error ? this.sanitizeError(error) : undefined
    });
  }

  /**
   * Log topic subscription attempt/result
   */
  logTopicSubscription(topic: string, success: boolean, error?: any): void {
    if (!this.isDebugEnabled()) return;

    const message = success
      ? `Subscribed to topic: ${topic}`
      : `Failed to subscribe to topic ${topic}: ${error?.message || 'Unknown error'}`;

    this.addLog('topic', message, {
      topic,
      success,
      error: error ? this.sanitizeError(error) : undefined
    });

    if (success && !this.subscribedTopics.includes(topic)) {
      this.subscribedTopics.push(topic);
      this.saveToStorage();
    }
  }

  /**
   * Log notification received
   */
  logNotificationReceived(notification: any): void {
    if (!this.isDebugEnabled()) return;

    const message = `Notification received: ${notification.title || 'No title'}`;
    this.addLog('notification', message, notification);

    // Store recent notifications (keep last 10)
    this.recentNotifications.unshift({
      ...notification,
      receivedAt: new Date().toISOString()
    });
    if (this.recentNotifications.length > this.MAX_NOTIFICATIONS) {
      this.recentNotifications = this.recentNotifications.slice(0, this.MAX_NOTIFICATIONS);
    }

    this.saveToStorage();
  }

  /**
   * Log error
   */
  logError(context: string, error: any): void {
    if (!this.isDebugEnabled()) return;

    const message = `Error in ${context}: ${error?.message || 'Unknown error'}`;
    this.addLog('error', message, {
      context,
      error: this.sanitizeError(error)
    });
  }

  /**
   * Log permission status check
   */
  logPermissionStatus(status: PermissionStatus): void {
    if (!this.isDebugEnabled()) return;

    const message = `Permission status: ${status.receive}`;
    this.addLog('permission', message, status);
  }

  /**
   * Get comprehensive debug info
   */
  async getDebugInfo(): Promise<DebugInfo> {
    const user = this.userService.getUser();
    const stylistID = user ? user.getID() : undefined;

    let permissionStatus: PermissionStatus | undefined;
    // Check permissions on native platforms (capacitor, hybrid, or cordova)
    if (Utilities.isCapacitorNative() || this.platform.is('capacitor') || this.platform.is('hybrid') || this.platform.is('cordova')) {
      try {
        permissionStatus = await PushNotifications.checkPermissions();
      } catch (error) {
        this.logError('getPermissionStatus', error);
      }
    }

    // Try to get current FCM token if we don't have one stored
    let currentToken = this.deviceToken;
    
    if (!currentToken && Utilities.isCapacitorNative() && this.platform.is('capacitor')) {
      try {
        const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
        const tokenResult = await FirebaseMessaging.getToken();
        currentToken = tokenResult.token;
        if (currentToken) {
          this.deviceToken = currentToken;
          // Don't change registration status when just fetching token
          // Only update token, preserve existing isRegisteredWithBackend
          this.saveToStorage();
        }
      } catch (error) {
        // Silently fail - token will be null
      }
    }

    const registered = this.isRegisteredWithBackend && !!currentToken;

    return {
      deviceToken: currentToken || undefined,
      deviceTokenMasked: currentToken ? this.maskToken(currentToken) : undefined,
      stylistID,
      platform: this.getPlatformInfo(),
      permissionStatus,
      registered: registered,
      subscribedTopics: [...this.subscribedTopics],
      recentNotifications: [...this.recentNotifications],
      logs: this.getLogs()
    };
  }

  /**
   * Clear all logs
   */
  clearLogs(): void {
    const logs: DebugLog[] = [];
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify({
      logs,
      deviceToken: this.deviceToken,
      isRegisteredWithBackend: this.isRegisteredWithBackend,
      subscribedTopics: this.subscribedTopics,
      recentNotifications: this.recentNotifications
    }));
  }

  /**
   * Export logs as JSON
   */
  exportLogs(): string {
    const debugInfo = {
      exportedAt: new Date().toISOString(),
      deviceToken: this.deviceToken ? this.maskToken(this.deviceToken) : null,
      subscribedTopics: this.subscribedTopics,
      recentNotifications: this.recentNotifications,
      logs: this.getLogs()
    };

    return JSON.stringify(debugInfo, null, 2);
  }

  /**
   * Re-register device with backend
   */
  async reregisterDevice(): Promise<Observable<any>> {
    // Check if running on native platform
    const isNative = this.isNativePlatform();
    
    if (!isNative) {
      return of({ success: false, message: 'Not running on native platform' });
    }

    try {
      // Get fresh token
      const tokenResult = await FirebaseMessaging.getToken();
      const deviceToken = tokenResult.token;

      // Log token retrieval (not registration yet - that happens after backend call succeeds)
      this.logRegistration(deviceToken, false);

      // Register with backend
      const user = this.userService.getUser();
      if (!user) {
        throw new Error('User not logged in');
      }

      const url = `${ENV_CONFIG.API_ROOT}/stylists/notifications/register`;
      const token = this.userService.getToken();
      if (!token) {
        this.logError('reregisterDevice', new Error('No authentication token available'));
        return of({ success: false, error: 'No authentication token available' });
      }
      
      const requestBody = {
        userID: user.getID(),
        deviceToken: deviceToken
      };
      
      // Use parent's post method which includes authentication headers
      return this.post(url, requestBody).pipe(
        map((response: any) => {
          this.logRegistration(deviceToken, true);
          // Handle both wrapped {data: ...} and direct response formats
          const result = response?.data || response;
          return { success: true, response: result };
        }),
        catchError((error) => {
          this.logError('reregisterDevice', error);
          return of({ success: false, error });
        })
      );
    } catch (error) {
      this.logError('reregisterDevice', error);
      return of({ success: false, error });
    }
  }

  /**
   * Check permissions
   */
  async checkPermissions(): Promise<PermissionStatus> {
    if (!this.isNativePlatform()) {
      return { receive: 'prompt' };
    }

    try {
      const status = await PushNotifications.checkPermissions();
      this.logPermissionStatus(status);
      return status;
    } catch (error) {
      this.logError('checkPermissions', error);
      return { receive: 'prompt' };
    }
  }

  // Private helper methods

  /**
   * Check if running on native platform (iOS or Android)
   */
  private isNativePlatform(): boolean {
    return Utilities.isCapacitorNative() || 
           this.platform.is('hybrid') || 
           this.platform.is('cordova') ||
           this.platform.is('capacitor') ||
           (this.platform.is('ios') && !this.platform.is('mobileweb')) ||
           (this.platform.is('android') && !this.platform.is('mobileweb'));
  }

  private addLog(type: DebugLog['type'], message: string, data?: any): void {
    const log: DebugLog = {
      timestamp: new Date().toISOString(),
      type,
      message,
      data
    };

    // Get logs without overwriting in-memory state
    const logs = this.getLogs();
    logs.unshift(log);

    // Keep only last MAX_LOGS
    if (logs.length > this.MAX_LOGS) {
      logs.splice(this.MAX_LOGS);
    }

    // Save logs along with current state (don't overwrite current token/registration status)
    this.saveToStorage({
      logs: logs,
      deviceToken: this.deviceToken,
      isRegisteredWithBackend: this.isRegisteredWithBackend,
      subscribedTopics: this.subscribedTopics,
      recentNotifications: this.recentNotifications
    });
  }

  private getLogs(): DebugLog[] {
    // Load from storage but don't overwrite current in-memory values
    // (loadFromStorage modifies this.deviceToken and other properties)
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed.logs || [];
      }
    } catch (error) {
      console.error('[FCM-DEBUG] Error loading logs from storage', error);
    }
    return [];
  }

  private loadFromStorage(): {
    logs: DebugLog[];
    deviceToken: string | null;
    isRegisteredWithBackend: boolean;
    subscribedTopics: string[];
    recentNotifications: any[];
  } {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Only update in-memory values if they're not already set (preserve current state)
        if (!this.deviceToken && parsed.deviceToken) {
          this.deviceToken = parsed.deviceToken;
        }
        // Preserve existing registration status if already set to true
        this.isRegisteredWithBackend = parsed.isRegisteredWithBackend === true ? true : (this.isRegisteredWithBackend || false);
        if (parsed.subscribedTopics && parsed.subscribedTopics.length > 0) {
          this.subscribedTopics = parsed.subscribedTopics;
        }
        if (parsed.recentNotifications && parsed.recentNotifications.length > 0) {
          this.recentNotifications = parsed.recentNotifications;
        }
        return parsed;
      }
    } catch (error) {
      console.error('[FCM-DEBUG] Error loading from storage', error);
    }

    return {
      logs: [],
      deviceToken: this.deviceToken || null,
      isRegisteredWithBackend: this.isRegisteredWithBackend || false,
      subscribedTopics: this.subscribedTopics || [],
      recentNotifications: this.recentNotifications || []
    };
  }

  private saveToStorage(data?: {
    logs: DebugLog[];
    deviceToken: string | null;
    isRegisteredWithBackend: boolean;
    subscribedTopics: string[];
    recentNotifications: any[];
  }): void {
    try {
      // Always use current state - don't try to preserve from storage
      // This ensures we save the actual current value of the flag
      const toStore = data || {
        logs: this.getLogs(),
        deviceToken: this.deviceToken,
        isRegisteredWithBackend: this.isRegisteredWithBackend, // Use current flag value directly
        subscribedTopics: this.subscribedTopics,
        recentNotifications: this.recentNotifications
      };
      
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(toStore));
    } catch (error) {
      console.error('[FCM-DEBUG] Error saving to storage', error);
    }
  }

  private maskToken(token: string): string {
    if (!token || token.length < 8) return token;
    return `${token.substring(0, 4)}...${token.substring(token.length - 4)}`;
  }

  private sanitizeError(error: any): any {
    if (!error) return error;
    return {
      message: error.message,
      code: error.code,
      name: error.name
    };
  }

  private getPlatformInfo(): string {
    const platforms: string[] = [];
    if (this.platform.is('ios')) platforms.push('iOS');
    if (this.platform.is('android')) platforms.push('Android');
    if (this.platform.is('capacitor')) platforms.push('Capacitor');
    if (this.platform.is('hybrid')) platforms.push('Hybrid');
    if (this.platform.is('cordova')) platforms.push('Cordova');
    if (platforms.length === 0) platforms.push('Web');
    return platforms.join(', ');
  }
}

